<template>
  <div class="sidebar">
    <div class="sidebar__title sf-pv sf-fr" @click="toggleNav">
      <div :class="{ padded: $slots.nav }" class="sidebar__title__inner">
        <slot name="title"/>
      </div>
      <div v-if="$slots.nav" :class="{ opened: navActive }" class="sidebar__caret sf-ml-s"/>
    </div>
    <div class="sidebar__content">
      <sf-slide-toggle v-if="$slots.nav" :value="navActive" :class="{ top: $slots.top }" class="sidebar__nav">
        <div :class="{ top: $slots.top }" class="sidebar__nav__inner">
          <slot name="nav"/>
        </div>
      </sf-slide-toggle>
      <div v-if="$slots.top" class="sidebar__top sf-cg sf-pv">
        <slot name="top"/>
      </div>
      <div :style="{ background }" class="sidebar__wrapper">
        <div :class="{ 'sf-cg': grid }" class="sidebar__left sf-pv-l">
          <slot/>
        </div>
      </div>

      <div class="sidebar__toggle sf-c" @click="toggleSidebar">
        <icon/>
      </div>
      <div class="sidebar__right" @transitionend="refresh">
        <div ref="sidebar">
          <slot name="right"/>
        </div>
        <div
          :class="{ sticky, padded: $ui.headerActive, active: sidebarActive }"
          class="sidebar__calc">
          <div :class="{ active: sidebarActive }" class="sidebar__calc__inner">
            <div class="sidebar__calc__title sf-fr sf-p-s sf-sb">
              <div>{{ title }}</div>
              <div class="sidebar__calc__close" @click="toggleSidebar"/>
            </div>
            <calculator v-if="showCalculator" ref="calc" :show-term-stl="showTermStl" :show-document-texts="showDocumentTexts" class="sidebar__calc__el"/>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  components: {
    Icon: {
      render(h) {
        return h('div', this.$tools.currency.local);
      },
    },
  },
  props: {
    showCalculator: {
      type: Boolean,
      default: true,
    },
    showTermStl: {
      type: Boolean,
      default: true,
    },
    showDocumentTexts: {
      type: Boolean,
      default: true,
    },
    grid: { type: Boolean, default: true },
    background: {
      type: String,
      default() {
        return this.$theme.colors['page-bg'];
      },
    },
  },
  data: () => ({
    sidebarActive: false,
    navActive: false,
    observer: false,
    sticky: false,
  }),
  computed: {
    // Override this method to use title
    title() {
      return '';
    },
  },
  watch: {
    $route() {
      this.navActive = false;
    },
  },
  mounted() {
    if (!('IntersectionObserver' in window)) return;
    this.observer = new IntersectionObserver(
      ([record], observer) => {
        this.sticky = record.intersectionRatio === 0;
      },
      { threshold: 0 }
    );

    this.observer.observe(this.$refs.sidebar);
  },
  methods: {
    toggleSidebar() {
      this.sidebarActive = !this.sidebarActive;
    },
    toggleNav() {
      this.navActive = !this.navActive;
    },
    closeNav() {
      this.navActive = false;
    },
    refresh() {
      if (this.$refs.calc) this.$refs.calc.refresh();
    },
  },
};
</script>

<style lang="scss" scoped>
$sidebarWidth: 440px;

%flex-column {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.sidebar {
  @extend %flex-column;

  &__title {
    @include mobile {
      background: map-get($map: $colors, $key: ternary);
      color: map-get($map: $colors, $key: text-reverse);
    }

    @include not-desktop {
      @include contentGrid;
    }

    @include tablet {
      border-bottom: 1px solid map-get($map: $colors, $key: page-bg);
      border-top: 1px solid map-get($map: $colors, $key: page-bg);
    }

    &__inner {
      @include desktop {
        @include contentGrid;
      }

      &.padded {
        @include tablet {
          margin-bottom: 20px;
        }
      }
    }
  }

  &__wrapper {
    @extend %flex-column;
  }

  &__content {
    @extend %flex-column;
    position: relative;

    p {
      text-align: justify;
    }
  }

  &__top {
    @include desktop {
      padding-right: $sidebarWidth;
    }
  }

  &__left {
    @extend %flex-column;

    &.sf-cg {
      @include desktop {
        padding-right: $sidebarWidth;
      }
    }
  }

  &__right {
    @include desktop {
      position: absolute;
      top: -20px;
      right: 20px;
      height: 100%;
    }

    @include aboveGrid {
      right: calc(100vw - #{$gridWidth} - (100vw - #{$gridWidth}) / 2 + #{$gridGutter});
    }
  }

  &__toggle {
    @include square(64px);
    position: fixed;
    right: 10px;
    z-index: 4;
    border-radius: 50%;
    font-size: 28px;
    background: map-get($map: $colors, $key: ternary);
    color: map-get($map: $colors, $key: text-reverse);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);

    @include mobile {
      bottom: 10px;
    }

    @include tablet-only {
      top: 100px;
    }

    @include desktop {
      display: none;
    }
  }

  &__calc {
    @include not-desktop {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 10;
      display: flex;
      justify-content: flex-end;
      transition: opacity 0.5s;
      background: rgba(black, 0.5);
      pointer-events: none;
      opacity: 0;

      &.active {
        opacity: 1;
      }
    }

    @include desktop {
      position: sticky;
      top: 20px;
      transition: transform 0.6s ease-in-out;

      &.sticky.padded {
        transform: translateY(50px);
      }
    }

    &__inner {
      pointer-events: auto;
      transition: transform 0.5s;

      &.active {
        transform: translate(0);
      }

      @include mobile {
        width: 100vw;
        transform: translateY(100%);
      }

      @include tablet-only {
        transform: translateX(100%);
      }

      @include not-desktop {
        height: 100%;
        max-height: 100vh;
        overflow: auto;
        background: #efefef;
        box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16);
      }
    }

    &__title {
      justify-content: space-between;

      @include desktop {
        display: none;
      }
    }

    &__close {
      @include closeButton;
    }

    &__el {
      @include not-desktop {
        box-shadow: none;
      }
    }
  }

  &__nav {
    @include contentGrid;
    position: relative;

    @include mobile {
      position: absolute;
      top: 0;
      left: 0;
      z-index: 2;
      background: map-get($map: $colors, $key: primary);
      color: map-get($map: $colors, $key: text-reverse);
    }

    @include tablet {
      height: auto !important;
      overflow: visible !important;

      &.top {
        margin-bottom: 20px;
      }
    }

    &__inner {
      @include tablet {
        position: absolute;
        top: 0;
        left: 20px;
        display: inline-flex;
        padding: map-get($map: $paddings, $key: xsmall);
        border-radius: 6px;
        transform: translateY(-50%);

        &.top {
          background: map-get($map: $colors, $key: page-bg);
        }

        &:not(.top) {
          background: #fff;
          border: 1px solid map-get($map: $colors, $key: border);
        }
      }

      /deep/ a {
        display: block;
        color: map-get($map: $colors, $key: text);
        text-decoration: none;
        border-radius: 6px;
        transition: all 0.3s;
        color: inherit;
        cursor: pointer;

        &:hover {
          text-decoration: underline;
        }

        @include mobile {
          padding-top: map-get($map: $paddings, $key: small);
          padding-bottom: map-get($map: $paddings, $key: small);
        }

        @include tablet {
          padding: map-get($map: $paddings, $key: xsmallM) map-get($map: $paddings, $key: small);
          margin-left: map-get($map: $margins, $key: xsmall);
        }

        &.active-url {
          @include mobile {
            text-decoration: underline;
          }

          @include tablet {
            background: #fff;
            font-weight: 600;
          }
        }

        &:first-child {
          margin: 0;
        }
      }
    }
  }

  &__caret {
    transform: rotate(45deg);
    transition: all 0.3s ease-in-out;
    border: solid #fff;
    border-width: 0 3px 3px 0;
    display: inline-block;
    padding: 4px;

    @include tablet {
      display: none;
    }

    &.opened {
      transform: rotate(-135deg);
    }
  }
}
</style>
